// Global currency store — shared across all consumers (header switcher, /pricing
// select, PriceBadge). Lifted out of useCurrency so all components see the same
// state in the same tab without depending on the storage event.
import { create } from "zustand";

export type CurrencyCode =
  | "EUR" | "USD" | "NGN" | "XOF" | "XAF" | "GNF" | "KES" | "GHS"
  | "BRL" | "MAD" | "MXN" | "ARS"
  | "EGP" | "ZAR" | "COP" | "IDR" | "PHP" | "VND" | "INR" | "GBP";

export const CURRENCIES: Record<CurrencyCode, { symbol: string; label: string }> = {
  EUR: { symbol: "€", label: "Euro" },
  USD: { symbol: "$", label: "US Dollar" },
  NGN: { symbol: "₦", label: "Nigerian Naira" },
  XOF: { symbol: "CFA ", label: "West African CFA Franc" },
  XAF: { symbol: "FCFA ", label: "Central African CFA Franc" },
  GNF: { symbol: "GNF ", label: "Guinean Franc" },
  KES: { symbol: "KSh ", label: "Kenyan Shilling" },
  GHS: { symbol: "GH₵ ", label: "Ghanaian Cedi" },
  BRL: { symbol: "R$", label: "Brazilian Real" },
  MAD: { symbol: "MAD ", label: "Moroccan Dirham" },
  MXN: { symbol: "MX$", label: "Mexican Peso" },
  ARS: { symbol: "ARS ", label: "Argentine Peso" },
  EGP: { symbol: "E£", label: "Egyptian Pound" },
  ZAR: { symbol: "R", label: "South African Rand" },
  COP: { symbol: "COL$", label: "Colombian Peso" },
  IDR: { symbol: "Rp", label: "Indonesian Rupiah" },
  PHP: { symbol: "₱", label: "Philippine Peso" },
  VND: { symbol: "₫", label: "Vietnamese Dong" },
  INR: { symbol: "₹", label: "Indian Rupee" },
  GBP: { symbol: "£", label: "British Pound" },
};

/** Clean rounding anchors per currency (in major units). */
export const ROUNDING_ANCHORS: Partial<Record<CurrencyCode, number>> = {
  NGN: 500, XOF: 500, XAF: 500, GNF: 5000, KES: 50, GHS: 5,
  BRL: 10, MAD: 10, MXN: 10, ARS: 500, EGP: 10, ZAR: 10,
  COP: 5000, IDR: 5000, PHP: 50, VND: 10000, INR: 50,
  USD: 1, EUR: 1, GBP: 1,
};

export function roundToAnchor(major: number, currency: CurrencyCode): number {
  const a = ROUNDING_ANCHORS[currency] ?? 1;
  if (a <= 0) return Math.round(major);
  return Math.round(major / a) * a;
}

const STORAGE_KEY = "bp.currency";
const MANUAL_FLAG_KEY = "bp.currency.manual";

function readStored(): CurrencyCode | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v in CURRENCIES ? (v as CurrencyCode) : null;
  } catch {
    return null;
  }
}

interface CurrencyState {
  currency: CurrencyCode;
  manuallySet: boolean;
  setCurrency: (c: CurrencyCode, opts?: { manual?: boolean }) => void;
  clearManual: () => void;
}

function readManualFlag(): boolean {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(MANUAL_FLAG_KEY) === "1"; } catch { return false; }
}

export const useCurrencyStore = create<CurrencyState>((set) => ({
  currency: readStored() ?? "EUR",
  manuallySet: readManualFlag(),
  setCurrency: (c, opts) => {
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem(STORAGE_KEY, c); } catch { /* noop */ }
      if (opts?.manual) {
        try { window.localStorage.setItem(MANUAL_FLAG_KEY, "1"); } catch { /* noop */ }
      }
    }
    set((s) => ({ currency: c, manuallySet: opts?.manual ? true : s.manuallySet }));
  },
  clearManual: () => {
    if (typeof window !== "undefined") {
      try { window.localStorage.removeItem(MANUAL_FLAG_KEY); } catch { /* noop */ }
    }
    set({ manuallySet: false });
  },
}));

// Cross-tab sync
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY && e.newValue && e.newValue in CURRENCIES) {
      useCurrencyStore.setState({ currency: e.newValue as CurrencyCode });
    }
  });
}

/**
 * Generic locale + currency price formatter.
 *
 * Takes only (amount, currency, locale) — no page, product or surface ever gets
 * a special case in here. Placement, grouping and separators come from
 * `Intl.NumberFormat(locale, { style: "currency" })`, so `en` renders
 * "€179.00" and `fr` renders "179,00 €" with the non-breaking space CLDR
 * prescribes. The only local adjustment is the display token for currencies
 * whose ISO code we present with a house symbol (CFA, FCFA, KSh, GH₵).
 */

/** Currencies displayed without decimals. */
const ZERO_DECIMAL_CURRENCIES: CurrencyCode[] = ["NGN", "XOF", "XAF", "GNF", "KES", "ARS"];

/** House display token replacing the ISO code Intl emits for these currencies. */
const CURRENCY_DISPLAY_OVERRIDES: Partial<Record<CurrencyCode, string>> = {
  XOF: "CFA",
  XAF: "FCFA",
  GNF: "GNF",
  KES: "KSh",
  GHS: "GH₵",
  MAD: "MAD",
};

/** Map an app language tag to a full BCP-47 locale for formatting. */
export function intlLocaleFor(locale: string = "en"): string {
  const lang = (locale || "en").slice(0, 2).toLowerCase();
  return lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US";
}

export function formatMinor(amountMinor: number, currency: CurrencyCode, locale: string = "en"): string {
  const noDecimals = ZERO_DECIMAL_CURRENCIES.includes(currency);
  const major = noDecimals ? Math.round(amountMinor / 100) : amountMinor / 100;
  const intlLocale = intlLocaleFor(locale);

  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2,
    });
  } catch {
    formatter = new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2,
    });
  }

  const override = CURRENCY_DISPLAY_OVERRIDES[currency];
  return formatter
    .formatToParts(major)
    .map((part) => (part.type === "currency" && override ? override : part.value))
    .join("");
}